
import { defineComponent } from "vue";
import DutiesService from "@/api/Security/DutiesService";
import AccessDutyListService from "@/api/Security/AccessDutyListService";
import RoutesService from "@/api/Administration/RoutesService";
import type { IDutiesModel, IAccessDutyListModel, IRoutesModel } from "@/interfaces/modules";
import store from "@/store";

export default defineComponent({
  name: "PermissionsByRoleScreen",
  data() {
    return {
      table: {
        loading: false,
      } as any,

      roles: [] as IDutiesModel[],
      routes: [] as IRoutesModel[],
      // Asignaciones actuales (rol seleccionado)
      assignments: [] as IAccessDutyListModel[],
      // Permisos marcados para eliminar
      removedAssignments: [] as IAccessDutyListModel[],

      selectedRoleId: null as number | null,
      menuFilter: null as string | null,
      search: "",
    };
  },
  computed: {
    roleOptions(): Array<{ value: number; text: string }> {
      return (this.roles || [])
        .map((r) => ({ value: r.Id, text: r.Name }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    menuOptions(): Array<{ value: string; text: string }> {
      const set = new Set<string>();
      for (const r of this.routes || []) {
        const name = r.MenuName || "Sin menú";
        set.add(name);
      }
      return Array.from(set)
        .map((name) => ({ value: name, text: name }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    // Estructura para el árbol Menú -> Rutas
    menusForTree(): Array<{ name: string; routes: IRoutesModel[] }> {
      if (!this.selectedRoleId) return [];

      const term = (this.search || "").toLowerCase().trim();

      const filteredRoutes = (this.routes || []).filter((r) => {
        const menuName = r.MenuName || "Sin menú";

        if (this.menuFilter && menuName !== this.menuFilter) return false;

        if (term) {
          const text = `${menuName} ${r.Name || ""}`.toLowerCase();
          if (!text.includes(term)) return false;
        }

        return true;
      });

      const map = new Map<string, IRoutesModel[]>();
      for (const r of filteredRoutes) {
        const key = r.MenuName || "Sin menú";
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(r);
      }

      return Array.from(map.entries())
        .map(([name, routes]) => ({
          name,
          routes: routes.sort((a, b) => (a.Name || "").localeCompare(b.Name || "")),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    currentRoleName(): string | null {
      if (!this.selectedRoleId) return null;
      const r = this.roles.find((x) => Number(x.Id) === Number(this.selectedRoleId));
      return r?.Name ?? null;
    },

    totalAssigned(): number {
      // Total asignado real (sin contar los pendientes de eliminar)
      return this.assignments.length;
    },

    canSave(): boolean {
      const hasNew = this.assignments.some((a) => !a.Id || Number(a.Id) === 0);
      const hasRemoved = this.removedAssignments.length > 0;
      return hasNew || hasRemoved;
    },
  },
  async mounted() {
    await this.loadRoles();
    await this.loadAllRoutes();
  },
  methods: {
    async loadRoles() {
      try {
        let _records = (await DutiesService.GetSystemDuties()) || [];
        this.roles = _records.filter((r) => !r.FullControl);
      } catch (e) {
        console.error("Error cargando roles:", e);
      }
    },
    async loadAllRoutes() {
      try {
        this.routes = (await RoutesService.GetRoute()) || [];
      } catch (e) {
        console.error("Error cargando permisos (rutas):", e);
      }
    },
    async onRoleChanged() {
      this.assignments = [];
      this.removedAssignments = [];
      this.menuFilter = null;
      this.search = "";

      if (!this.selectedRoleId) return;

      await this.loadAssignments();
    },
    async loadAssignments() {
      if (!this.selectedRoleId) return;

      try {
        this.table.loading = true;
        const result =
          (await AccessDutyListService.GetAccessDutytListDutiesbyRolId(
            this.selectedRoleId
          )) || [];
        this.assignments = result as IAccessDutyListModel[];
        this.removedAssignments = [];
      } catch (e) {
        console.error("Error cargando permisos por rol:", e);
        store.dispatch("core/popupDialog", {
          text: "No se pudieron cargar los permisos del rol.",
          type: "error",
          showCancelButton: false,
        });
      } finally {
        this.table.loading = false;
      }
    },

    // Helpers de asignaciones
    assignmentForRoute(route: IRoutesModel): IAccessDutyListModel | undefined {
      return this.assignments.find(
        (a) => Number(a.RouteId) === Number(route.Id)
      );
    },

    isRemoved(route: IRoutesModel): boolean {
      return this.removedAssignments.some(
        (a) => Number(a.RouteId) === Number(route.Id)
      );
    },

    isAssigned(route: IRoutesModel): boolean {
      // Asignado solo si existe en assignments y no está marcado para eliminar
      return !!this.assignmentForRoute(route) && !this.isRemoved(route);
    },

    statusLabel(route: IRoutesModel): string {
      if (this.isRemoved(route)) return "Pendiente eliminar";

      const a = this.assignmentForRoute(route);
      if (!a) return "Sin asignar";
      if (a.Id && Number(a.Id) > 0) return "Asignado";
      return "Pendiente guardar";
    },

    statusBadgeClass(route: IRoutesModel): string {
      if (this.isRemoved(route)) return "bg-danger";

      const a = this.assignmentForRoute(route);
      if (!a) return "bg-secondary";
      if (a.Id && Number(a.Id) > 0) return "bg-info";
      return "bg-warning text-dark";
    },

    assignedCountForMenu(menuName: string): number {
      const routesForMenu = (this.routes || []).filter(
        (r) => (r.MenuName || "Sin menú") === menuName
      );
      const routeIds = new Set(routesForMenu.map((r) => Number(r.Id)));
      return this.assignments.filter(
        (a) =>
          routeIds.has(Number(a.RouteId)) &&
          !this.removedAssignments.some((r) => Number(r.RouteId) === Number(a.RouteId))
      ).length;
    },

    onTogglePermission(route: IRoutesModel, newVal: boolean) {
      if (!this.selectedRoleId) {
        store.dispatch("core/popupDialog", {
          text: "Debe seleccionar un rol antes de asignar permisos.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      const existing = this.assignmentForRoute(route);

      if (newVal) {
        // Si estaba marcado para eliminar, lo sacamos de la lista de eliminados
        this.removedAssignments = this.removedAssignments.filter(
          (a) => Number(a.RouteId) !== Number(route.Id)
        );

        // Si no existe en assignments, lo creamos como nuevo
        if (!existing) {
          this.assignments.push({
            Id: 0, // nuevo
            DutyId: this.selectedRoleId,
            RouteId: route.Id,
            MenuName: route.MenuName,
            RouteName: route.Name,
          } as IAccessDutyListModel);
        }
      } else {
        // Desmarcar
        if (existing) {
          if (existing.Id && Number(existing.Id) > 0) {
            // Ya está en BD → marcar para eliminar
            const alreadyMarked = this.removedAssignments.some(
              (a) => Number(a.RouteId) === Number(route.Id)
            );
            if (!alreadyMarked) {
              this.removedAssignments.push(existing);
            }
          } else {
            // Nuevo sin guardar → simplemente quitarlo de assignments
            this.assignments = this.assignments.filter(
              (a) => Number(a.RouteId) !== Number(route.Id)
            );
          }
        } else {
          // No existe y está desmarcado → no hacemos nada
        }
      }
    },

    async saveAll() {
      if (!this.selectedRoleId) return;

      const recordsToSave: IAccessDutyListModel[] = this.assignments
        .filter((a) => !a.Id || Number(a.Id) === 0)
        .map((a) => ({
          Id: 0,
          DutyId: this.selectedRoleId!,
          RouteId: a.RouteId,
        } as IAccessDutyListModel));

      const recordsToDelete = this.removedAssignments.filter(
        (a) => a.Id && Number(a.Id) > 0
      );

      if (!recordsToSave.length && !recordsToDelete.length) {
        store.dispatch("core/popupDialog", {
          text: "No hay cambios para guardar.",
          type: "info",
          showCancelButton: false,
        });
        return;
      }

      try {
        this.table.loading = true;

        // Guardar nuevos
        if (recordsToSave.length) {
          await AccessDutyListService.PostAccessDutytListDuties(recordsToSave);
        }

        // Eliminar permisos
        if (recordsToDelete.length) {
          for (const del of recordsToDelete) {
            await AccessDutyListService.DeleteAccessDutytListDuties(del.Id as number);
          }
        }

        store.dispatch("core/popupDialog", {
          text: "Permisos actualizados correctamente.",
          type: "success",
          showCancelButton: false,
        });

        // Recargar estado desde base de datos
        await this.loadAssignments();
      } catch (error) {
        console.error("Error al guardar permisos:", error);
        store.dispatch("core/popupDialog", {
          text: "No se pudo guardar los cambios de permisos. Contacte al administrador.",
          type: "error",
          showCancelButton: false,
        });
      } finally {
        this.table.loading = false;
      }
    },
  },
});
